import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/useAuthStore';
import { readLicenseFromStorage } from '@/utils/licenseStorage';
import {
  isDemoCrudBlockedPath,
  isLicenseDemo,
  showDemoAccountBlockedModal,
} from '@/utils/demoAccount';

const ALLOW_CLOSEST =
  [
    '.ant-pagination',
    '.ant-tabs-nav',
    '.ant-table-column-sorters',
    '.ant-table-filter-trigger',
    '.ant-modal-root',
    '.ant-modal-wrap',
    '.ant-modal-confirm',
    '.ant-modal-close',
    '.ant-drawer-close',
    '.ant-popover-inner',
    '.ant-message',
    '.ant-notification',
    '[data-demo-allow="true"]',
    'a[href]:not(.ant-btn)',
    '.ant-menu',
    '.ant-breadcrumb',
  ].join(', ');

const INTERACTIVE_CLOSEST =
  [
    'button',
    '.ant-btn',
    '[role="button"]',
    '.ant-upload',
    '.ant-switch',
    'input[type="file"]',
  ].join(', ');

/**
 * Bắt click CRUD trên các page có CheckDemo (V1) khi license demo.
 * Gắn `data-demo-allow="true"` trên nút xem/đọc nếu cần bỏ qua.
 */
export const DemoCrudClickGuard: React.FC = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const tenantID = useAuthStore((s) => s.tenantID);

  useEffect(() => {
    const path = router.asPath || router.pathname || '';
    if (!isDemoCrudBlockedPath(path)) return;

    const license = tenantID ? readLicenseFromStorage(tenantID) : undefined;
    if (!isLicenseDemo(license ?? null)) return;

    let isAlertOpen = false;

    const onClickCapture = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest) return;

      // Không bắt click trong modal/alert đang mở (ví dụ nút "Đã hiểu")
      if (target.closest(ALLOW_CLOSEST)) return;
      if (isAlertOpen || document.querySelector('.ant-modal-confirm, .ant-modal-root .ant-modal-wrap')) {
        return;
      }

      const interactive = target.closest(INTERACTIVE_CLOSEST) as HTMLElement | null;
      if (!interactive) return;

      // Nút điều hướng bước BSC "Tiếp tục" — không chặn
      if (
        interactive.closest('#btntieptuc, #btnnext') ||
        interactive.id === 'btntieptuc' ||
        interactive.id === 'btnnext'
      ) {
        return;
      }

      const labelText = (interactive.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (
        labelText === 'tiếp tục' ||
        labelText.includes('tiếp tục') ||
        labelText === 'tiep tuc'
      ) {
        return;
      }

      // Cho phép nút xem (eye) / tải xuống thuần đọc
      if (
        interactive.querySelector?.('.anticon-eye, .anticon-download, .anticon-search, .anticon-reload, .anticon-sync') ||
        interactive.classList.contains('anticon-eye') ||
        interactive.classList.contains('anticon-download')
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      isAlertOpen = true;
      showDemoAccountBlockedModal(t);
      window.setTimeout(() => {
        isAlertOpen = false;
      }, 500);
    };

    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, [router.asPath, router.pathname, tenantID, t]);

  return null;
};
